import {
  Box,
  Paper,
  Typography,
  Stack,
  Avatar,
  IconButton,
  Tooltip,
  Button,
} from "@mui/material";
import SmartToyIcon from "@mui/icons-material/AutoAwesome";
import PersonIcon from "@mui/icons-material/Person";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import { motion } from "framer-motion";
import MessageInput from "../components/MessageInput";
import { useState, useRef, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";

const MotionBox = motion(Box);
const TypingDots = () => <span className="typing-dots">Processing request</span>;
const isJsonText = (text) => {
  if (!text) return false;
  const t = text.trim();
  return t.startsWith("{") || t.startsWith("[");
};
const tryParseJson = (text) => {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

export default function ChatPage() {
  const [messages, setMessages] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const chatEndRef = useRef(null);
  const wsRef = useRef(null);
  const processingIdRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    const connectWebSocket = () => {
      //wsRef.current = new WebSocket("ws://localhost:5099/ws/chat");
      wsRef.current = new WebSocket("wss://mcp.247sites.in/agent/ws/chat");
      

      wsRef.current.onopen = () => console.log("✅ WebSocket connected");
      wsRef.current.onclose = () => console.log("❌ WebSocket disconnected");

      wsRef.current.onmessage = (event) => {
        const data = JSON.parse(event.data);
        const { message, isFinal } = data;

        const parsed = typeof message === "string" ? tryParseJson(message) : null;
       if (parsed?.action === "db_export" && parsed.url) {
          const fullUrl = `${window.location.origin}${parsed.url}`;

  setMessages((prev) => [
    ...prev.filter((m) => !m.isProcessing),
    {
      id: uuidv4(),
      steps: [
        {
          text: `📥 CSV ready (${parsed.count} records)`,
          downloadUrl: fullUrl,
          fileName: parsed.fileName,
        },
      ],
      time: new Date(),
    },
  ]);

  setIsProcessing(false);
  processingIdRef.current = null;
  return;
}
        setMessages((prev) =>
          prev.map((msg) => {
            if (!msg.isProcessing) return msg;
            const currentText =
              msg.steps[0]?.text === "Processing request…"
                ? message
                : (msg.steps[0]?.text || "") + message;
            return { ...msg, steps: [{ text: currentText }] };
          })
        );

        if (isFinal) {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.isProcessing ? { ...msg, isProcessing: false } : msg
            )
          );
          setIsProcessing(false);
          processingIdRef.current = null;
        }
      };
    };

    connectWebSocket();

    return () => {
      wsRef.current?.close();
    };
  }, []);
  const copyToClipboard = (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
    } else fallbackCopy(text);
  };
  const fallbackCopy = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    document.execCommand("copy");
    document.body.removeChild(textArea);
  };

  const stopProcessing = () => {
    if (!processingIdRef.current) return;

    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === processingIdRef.current
          ? { ...msg, isProcessing: false }
          : msg
      )
    );

    setIsProcessing(false);

    wsRef.current?.close();
    setTimeout(() => {
      wsRef.current = new WebSocket("ws://localhost:5099/ws/chat");
      wsRef.current.onopen = () => console.log("✅ WebSocket reconnected");

      wsRef.current.onmessage = (event) => {
        const data = JSON.parse(event.data);
        const { message, isFinal } = data;

        setMessages((prev) =>
          prev.map((msg) => {
            if (!msg.isProcessing) return msg;
            const currentText =
              msg.steps[0]?.text === "Processing request…"
                ? message
                : (msg.steps[0]?.text || "") + message;
            return { ...msg, steps: [{ text: currentText }] };
          })
        );

        if (isFinal) {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.isProcessing ? { ...msg, isProcessing: false } : msg
            )
          );
          setIsProcessing(false);
          processingIdRef.current = null;
        }
      };
    }, 100);

    processingIdRef.current = null;
  };
  const sendMessage = (text) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error("WebSocket not connected");
      return;
    }

    if (isProcessing) stopProcessing();

    const userId = uuidv4();
    const processingId = uuidv4();
    processingIdRef.current = processingId;

    setMessages((prev) => [
      ...prev,
      { id: userId, text, steps: [], time: new Date() },
      {
        id: processingId,
        steps: [{ text: "Processing request…" }],
        isProcessing: true,
        time: new Date(),
      },
    ]);

    setIsProcessing(true);
    wsRef.current.send(JSON.stringify({ message: text }));
  };

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "#f0f4f8",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          height: 64,
          px: 3,
          display: "flex",
          alignItems: "center",
          background: "linear-gradient(135deg, #1E88E5, #00BCD4)",
          color: "#fff",
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <SmartToyIcon sx={{ color: "#fff" }} />
          <Typography variant="h6" fontWeight={800} letterSpacing={1}>
            INFOMAZE
          </Typography>
        </Stack>
      </Box>

      {/* Messages */}
      <Box sx={{ flex: 1, overflowY: "auto", p: 3 }}>
        <Stack spacing={3} maxWidth={900} mx="auto">
          {messages.map((msg) => (
            <MotionBox
              key={msg.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {/* User Message */}
              {msg.text && (
                <Stack direction="row" justifyContent="flex-end" spacing={1}>
                  <Paper
                    sx={{
                      p: 2,
                      background: "linear-gradient(135deg, #2196F3, #1976D2)",
                      color: "#fff",
                      borderRadius: "15px",
                      maxWidth: "65%",
                    }}
                  >
                    {msg.text}
                  </Paper>
                  <Avatar
                    sx={{
                      background: "linear-gradient(135deg, #1976D2, #2196F3)",
                    }}
                  >
                    <PersonIcon />
                  </Avatar>
                </Stack>
              )}

              {/* Agent Message */}
              {msg.steps?.length > 0 && (
                <Stack direction="row" spacing={1} mt={2}>
                  <Avatar
                    sx={{
                      background: "linear-gradient(135deg, #000000, #000000)",
                    }}
                  >
                    <SmartToyIcon sx={{ color: "#fff" }} />
                  </Avatar>
                  <Stack spacing={1} flex={1}>
                    {msg.steps.map((step, index) => {
                      const isJson = isJsonText(step.text);
                      return (
                        <Paper
                          key={index}
                          sx={{
                            p: 1.5,
                            background: step.isError
                              ? "linear-gradient(135deg, #D32F2F, #F44336)"
                              : isJson
                              ? "linear-gradient(135deg, #424242, #616161)"
                              : "linear-gradient(135deg, #4CAF50, #388E3C)",
                            color: "#fff",
                            borderRadius: "15px",
                            whiteSpace: "pre-wrap",
                            fontFamily: isJson ? "monospace" : "inherit",
                            fontSize: isJson ? "0.85rem" : "1rem",
                            position: "relative",
                          }}
                        >
                          {step.text === "Processing request…" && msg.isProcessing ? (
                            <TypingDots />
                          ) : (
                            <>
                              {step.text}
                              {step.downloadUrl && (
                                <Box mt={1}>
                                  <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<DownloadIcon />}
                                    component="a"
                                    href={step.downloadUrl}
                                    download={step.fileName}
                                    sx={{
                                      backgroundColor: "#4CAF50",
                                      color: "#fff",
                                      textTransform: "none",
                                      "&:hover": { backgroundColor: "#43A047" },
                                    }}
                                  >
                                    Download CSV
                                  </Button>
                                </Box>
                              )}
                            </>
                          )}
                          {isJson && (
                            <Tooltip title="Copy JSON">
                              <IconButton
                                size="small"
                                onClick={() => copyToClipboard(step.text)}
                                sx={{
                                  position: "absolute",
                                  top: 4,
                                  right: 4,
                                  color: "#94a3b8",
                                }}
                              >
                                <ContentCopyIcon fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Paper>
                      );
                    })}
                  </Stack>
                </Stack>
              )}
            </MotionBox>
          ))}
          <div ref={chatEndRef} />
        </Stack>
      </Box>

      {/* Input */}
      <Box sx={{ p: 2 }}>
        <MessageInput
          onSend={sendMessage}
          disabled={isProcessing}
          onStop={stopProcessing}
        />
      </Box>

      <style>{`
        .typing-dots::after {
          content: '';
          animation: dots 1.5s steps(3, end) infinite;
          margin-left: 4px;
        }
        @keyframes dots {
          0%,20%{content:''}
          40%{content:'.'}
          60%{content:'..'}
          80%,100%{content:'...'}
        }
      `}</style>
    </Box>
  );
}